import { Component, OnInit, ViewChild } from '@angular/core';
import { ConfirmationService, LazyLoadEvent, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { HttpService } from 'src/app/services/http.service';
import { environment } from 'src/environments/environment';
import { FILTERS_TYPES, createQuery } from 'src/app/utils/filter';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { IntervalSubscriptionOptions } from 'src/app/utils/constants';

const fieldTypeMapper: any = {
  hubName: FILTERS_TYPES.TEXT,
  userName: FILTERS_TYPES.TEXT,
  userEmail: FILTERS_TYPES.TEXT,
  id: FILTERS_TYPES.TEXT,
  paidAmount: FILTERS_TYPES.NUMERIC,
  interval: FILTERS_TYPES.TEXT,
  subscriptionStatus: FILTERS_TYPES.TEXT,
  subscriptionPlan: FILTERS_TYPES.TEXT,
  createdOn: FILTERS_TYPES.DATE,
};

@Component({
  selector: 'app-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.scss'],
})
export class SubscriptionsComponent implements OnInit {
  @ViewChild('itemsTable') public dataTable: Table | undefined;

  intervalSubscriptionOptionsTypes = Object.keys(IntervalSubscriptionOptions).map((key: string) => ({
    label: IntervalSubscriptionOptions[key],
    value: key,
  }));

  IntervalSubscriptionOptions = IntervalSubscriptionOptions;

  data: any = [];
  opsOptions: { label: string; value: string }[] = [];
  private assignmentByHubId = new Map<string, string>();
  private readonly opsRoyalBase = `${environment.apiUrl}/admin-center/ops-royal/v1`;

  private readonly apiUrl = environment.apiUrl;
  private readonly appUrl = environment.appUrl;

  form: any;
  isLoadingForm: boolean = false;
  isDialogOpen: boolean = false;
  isEditMode: boolean = false;

  isCreateHubDialogOpen = false;
  isCreatingHub = false;
  createHubSubscriber: any = null;
  createHubName = '';
  createUrlHandler = '';
  createOpsEmail: string | null = null;

  isLoading: boolean = false;
  pageIndex: number = 0;
  pageSize: number = 15;
  firstInPage: number = 0;
  totalRecords: number = 200;
  lastQuery: string = '';
  selectedItem: any;

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
  ) {}

  cols = [
    { field: 'hubName', header: 'Hub' },
    { field: 'userName', header: 'User' },
    { field: 'subscriptionStatus', header: 'Status' },
    { field: 'subscriptionPlan', header: 'Type' },
    { field: 'interval', header: 'Plan' },
    { field: 'paidAmount', header: 'Amount' },
    { field: 'id', header: 'Payment' },
    { field: 'createdOn', header: 'Create On' },
  ];

  ngOnInit() {
    this.form = this.fb.group({
      id: [''],
      hubId: [''],
      hubName: [''],
      hubUrlHandler: [''],
      userId: [''],
      userName: [''],
      userEmail: [''],
      paidAmount: [0],
      interval: [''],
      subscriptionStatus: [''],
      subscriptionPlan: [''],
      createdOn: [null],
    });
    this.loadOpsOptions();
  }

  private setForm(item: any) {
    this.selectedItem = item;

    this.form.patchValue({ id: item.id });
    this.form.patchValue({ hubId: item.hubId });
    this.form.patchValue({ hubName: item.hubName });
    this.form.patchValue({ hubUrlHandler: item.hubUrlHandler });
    this.form.patchValue({ userId: item.userId });
    this.form.patchValue({ userName: item.userName });
    this.form.patchValue({ userEmail: item.userEmail });
    this.form.patchValue({ paidAmount: item.paidAmount });
    this.form.patchValue({ interval: item.interval });
    this.form.patchValue({ subscriptionStatus: item.subscriptionStatus });
    this.form.patchValue({ subscriptionPlan: item.subscriptionPlan });
    this.form.patchValue({ createdOn: item.createdOn ? new Date(item.createdOn) : null });

    this.form.markAsPristine();
  }

  hasFormErrors() {
    return !this.form.valid;
  }

  fieldErrors(field: string) {
    const controlState = this.form.controls[field];
    return controlState.dirty && controlState.errors ? controlState.errors : null;
  }

  loadData(event: LazyLoadEvent) {
    this.isLoading = true;
    this.firstInPage = event.first || 0;
    this.pageSize = event.rows || 0;
    this.pageIndex = this.pageSize > 0 ? Math.floor(this.firstInPage / this.pageSize) : 0;
    // Normalize global filter so createQuery always sees { value }.
    // PrimeNG may pass the input element, a string, or FilterMetadata.
    const globalRaw: any = event.globalFilter;
    let globalValue: string | null = null;
    if (typeof globalRaw === 'string') {
      globalValue = globalRaw;
    } else if (globalRaw && typeof globalRaw === 'object') {
      if (typeof globalRaw.value === 'string') {
        globalValue = globalRaw.value;
      } else if (typeof (globalRaw as HTMLInputElement).value === 'string' && (globalRaw as any).tagName) {
        globalValue = (globalRaw as HTMLInputElement).value;
      }
    }
    const eventForQuery = {
      ...event,
      filters: event.filters || {},
      globalFilter: globalValue ? { value: globalValue } : globalRaw,
    };
    const querySearch: string = createQuery(eventForQuery, fieldTypeMapper);
    const URL = `${this.opsRoyalBase}/subscriptions?${querySearch}page=${this.pageIndex}&size=${this.pageSize}`;
    this.lastQuery = URL;
    this.search(URL);
  }

  async search(url: string) {
    const el = document.getElementsByClassName('page-container')[0];
    if (el) {
      el.scrollTop = 0;
    }

    try {
      const response = await this.httpService.doGet(url).toPromise();
      if (response) {
        this.data = (response.content || []).map((row: any) => ({ ...row, id: row.subscriptionId, assignedOpsEmail: row.assignedOpsEmail }));
        this.totalRecords = response.totalElements;
      } else {
        this.data = [];
        this.totalRecords = 0;
        this.firstInPage = 0;
        this.pageIndex = 0;
      }
      this.isLoading = false;
    } catch (error: any) {
      this.data = [];
      this.totalRecords = 0;
      this.firstInPage = 0;
      this.pageIndex = 0;
      this.isLoading = false;
      console.log('Error: ', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message });
    }
  }

  private loadOpsOptions(): void {
    this.httpService.doGet(`${this.opsRoyalBase}/portal-operators?role=OPS`).subscribe({
      next: (rows) => {
        this.opsOptions = (rows || [])
          .filter((r: any) => r.active)
          .map((r: any) => ({ label: r.email, value: r.email }));
      },
      error: () => {
        this.opsOptions = [];
      },
    });
  }

  private async refreshAssignments(): Promise<void> {
    try {
      const rows = await this.httpService.doGet(`${this.opsRoyalBase}/assigned-hubs`).toPromise();
      this.assignmentByHubId = new Map();
      for (const row of rows || []) {
        if (row?.hubId && row?.assignedOpsEmail) {
          this.assignmentByHubId.set(row.hubId, row.assignedOpsEmail);
        }
      }
    } catch {
      this.assignmentByHubId = new Map();
    }
  }

  private withOpsAssignment(row: any): any {
    const hubId = this.normalizeHubId(row?.hubId);
    return {
      ...row,
      hubId,
      assignedOpsEmail: hubId ? this.assignmentByHubId.get(hubId) || null : null,
    };
  }

  /** Treat blank/whitespace hub ids as missing so Create hub UI appears. */
  hasHub(item: any): boolean {
    return !!this.normalizeHubId(item?.hubId);
  }

  private normalizeHubId(hubId: any): string | null {
    if (hubId == null) {
      return null;
    }
    const s = String(hubId).trim();
    return s ? s : null;
  }

  openCreateHubDialog(item: any): void {
    if (!item?.userId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing user',
        detail: 'This subscription has no customer user id.',
      });
      return;
    }
    this.createHubSubscriber = item;
    this.createHubName = item.userName || item.userEmail || 'Royal Hub';
    const seed = (item.userEmail || item.userName || 'royal')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
      .slice(0, 20);
    this.createUrlHandler = seed || 'royalhub';
    this.createOpsEmail = null;
    this.isCreateHubDialogOpen = true;
    // Close details dialog if open so create modal is not buried.
    this.isDialogOpen = false;
  }

  submitCreateHub(): void {
    if (!this.createHubSubscriber?.userId || !this.createHubName.trim() || !this.createUrlHandler.trim() || !this.createOpsEmail) {
      return;
    }
    this.isCreatingHub = true;
    this.httpService
      .doPost(`${this.opsRoyalBase}/customers/${this.createHubSubscriber.userId}/hubs`, {
        name: this.createHubName.trim(),
        urlHandler: this.createUrlHandler.trim().toLowerCase(),
        opsEmail: this.createOpsEmail,
        assignCallerAsOps: false,
      })
      .subscribe({
        next: (hub) => {
          this.isCreatingHub = false;
          this.isCreateHubDialogOpen = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Hub created',
            detail: `${hub?.name || 'Hub'} assigned to ${this.createOpsEmail}`,
          });
          this.refreshTable();
        },
        error: (err) => {
          this.isCreatingHub = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.details || err?.error?.message || 'Create hub failed',
          });
        },
      });
  }

  onAssignOps(item: any, opsEmail: string | null): void {
    if (!opsEmail) {
      this.unassignOps(item);
      return;
    }
    if (item?.projectId) {
      this.httpService
        .doPost(`${this.opsRoyalBase}/royal-projects/${item.projectId}/assigned-ops`, { opsEmail })
        .subscribe({
          next: () => {
            item.assignedOpsEmail = opsEmail;
            this.messageService.add({
              severity: "success",
              summary: "Assigned",
              detail: `${opsEmail} assigned to project`,
            });
          },
          error: (err) =>
            this.messageService.add({
              severity: "error",
              summary: "Error",
              detail: err?.error?.details || err?.error?.message || "Assign failed",
            }),
        });
      return;
    }
    if (!this.hasHub(item)) {
      this.openCreateHubDialog(item);
      return;
    }
    this.httpService
      .doPost(`${this.opsRoyalBase}/hubs/${item.hubId}/assigned-ops`, { opsEmail })
      .subscribe({
        next: (dto) => {
          item.assignedOpsEmail = dto.assignedOpsEmail;
          this.messageService.add({
            severity: "success",
            summary: "Assigned",
            detail: `${dto.assignedOpsEmail} -> ${item.hubName || item.hubId}`,
          });
        },
        error: (err) =>
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: err?.error?.details || err?.error?.message || "Assign failed",
          }),
      });
  }

  unassignOps(item: any): void {
    if (item?.projectId) {
      this.confirmationService.confirm({
        message: "Unassign Ops from this Royal project?",
        header: "Unassign Ops",
        icon: "pi pi-exclamation-triangle",
        accept: () => {
          this.httpService
            .doDelete(`${this.opsRoyalBase}/royal-projects/${item.projectId}/assigned-ops`)
            .subscribe({
              next: () => {
                item.assignedOpsEmail = null;
                this.messageService.add({ severity: "success", summary: "Unassigned" });
              },
              error: (err) =>
                this.messageService.add({
                  severity: "error",
                  summary: "Error",
                  detail: err?.error?.details || err?.error?.message || "Unassign failed",
                }),
            });
        },
      });
      return;
    }
    if (!item?.hubId) {
      return;
    }
    this.confirmationService.confirm({
      message: `Unassign Ops from ${item.hubName || item.hubId}?`,
      header: "Unassign Ops",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.httpService.doDelete(`${this.opsRoyalBase}/hubs/${item.hubId}/assigned-ops`).subscribe({
          next: () => {
            item.assignedOpsEmail = null;
            this.messageService.add({ severity: "success", summary: "Unassigned" });
          },
          error: (err) =>
            this.messageService.add({
              severity: "error",
              summary: "Error",
              detail: err?.error?.details || err?.error?.message || "Unassign failed",
            }),
        });
      },
    });
  }

  refreshTable() {
    this.search(this.lastQuery);
  }

  clear(table: Table) {
    table.clear();
  }

  openNew() {
    this.form.reset();
    this.isEditMode = true;
    this.isDialogOpen = true;
  }

  viewItem(item: any) {
    const id = item?.subscriptionId || item?.id;
    if (!id) {
      return;
    }
    void this.router.navigate(["/base/subscriptions", id]);
  }

  hideDialog() {
    this.isDialogOpen = false;
    this.isEditMode = false;
  }

  getHubUrl(item: any) {
    return `${this.appUrl}/${item.hubUrlHandler}/client-view?hubId=${item.hubId}`;
  }

  getStripeSubUrl(item: any) {
    return `https://dashboard.stripe.com/test/subscriptions/${item.id}`;
  }

  getFormFieldValue(field: string): any {
    return this.form.get(field).value;
  }

  displayType(item: any): string {
    return item?.subscriptionPlan || IntervalSubscriptionOptions[item?.interval] || item?.interval || '—';
  }
}
